import React, { useContext, useEffect, useState } from 'react';
import { API } from 'aws-amplify';
import operationData from '../../../../../Operation/data.json';
import { Context } from '../../../../Context';
import FoodDeliveryTabs from './FoodDeliveryTabs';

const API_NAME = 'foodDelivery';
const INDEX = '0';
const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];

const StoreTiming = () => {
  const { institutionData } = useContext(Context);
  const institution = operationData.foodDeliveryInstitution;
  const activeInstitution = institution || institutionData?.institution || '';

  const [timings, setTimings] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchTimings = async () => {
    if (!activeInstitution) return;
    setLoading(true);
    try {
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      const res = await API.get(API_NAME, path, { queryStringParameters: { type: 'timing' } });
      const data = res?.timingSettings?.weekdays || res?.data?.weekdays || res?.weekdays || {};
      const normalized = {};
      DAYS.forEach(d => { normalized[d] = { isOpen: true, slots: [{ open: '09:00', close: '22:00' }] }; });
      Object.keys(data || {}).forEach(day => { normalized[day] = data[day]; });
      setTimings(normalized);
    } catch (err) {
      setError(err?.message || 'Failed to load timings');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchTimings(); }, [activeInstitution]);

  const updateSlot = (day, idx, field, value) => {
    setTimings((prev) => ({ ...prev, [day]: { ...(prev[day]||{}), slots: (prev[day]?.slots||[]).map((s,i) => i===idx?{...s,[field]:value}:s) } }));
  };

  const toggleOpen = (day) => setTimings((prev) => ({ ...prev, [day]: { ...(prev[day]||{}), isOpen: !(prev[day]?.isOpen) } }));

  const addSlot = (day) => setTimings((prev) => ({ ...prev, [day]: { ...(prev[day]||{}), slots: [...(prev[day]?.slots||[]), { open: '09:00', close: '22:00' }] } }));

  const removeSlot = (day, idx) => setTimings((prev) => ({ ...prev, [day]: { ...(prev[day]||{}), slots: (prev[day]?.slots||[]).filter((_,i)=>i!==idx) } }));

  const save = async () => {
    setLoading(true);
    setError('');
    try {
      const cleaned = { weekdays: timings, format: 'slots_array' };
      const path = `/admin/settings/${activeInstitution}/${INDEX}`;
      await API.post(API_NAME, path, { queryStringParameters: { type: 'timing' }, body: cleaned });
      await fetchTimings();
    } catch (err) {
      setError(err?.message || 'Failed to save timings');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50">
      <FoodDeliveryTabs />
      <div className="px-4 py-6 lg:px-8">
        <div className="mx-auto max-w-4xl space-y-6">
          {/* Header */}
          <div className="bg-white px-6 py-6 rounded-lg shadow-sm border border-gray-200">
            <h1 className="text-2xl font-extrabold text-[#1F2937]">Food Delivery — Store Timing</h1>
            <p className="mt-1 text-sm text-gray-500">Configure opening hours and multiple service slots per day • {activeInstitution}</p>
          </div>

          {/* Messages */}
          {error && (
            <div className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {error}
            </div>
          )}
          {loading && (
            <div className="px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-700">
              ⏳ Updating store timing...
            </div>
          )}

          {/* Timing Configuration */}
          <div className="space-y-3">
            {DAYS.map((day) => {
              const d = timings[day] || { isOpen: true, slots: [{ open: '09:00', close: '22:00' }] };
              return (
                <div key={day} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 transition hover:border-gray-400">
                  {/* Day Header */}
                  <div className="flex items-center justify-between mb-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={d.isOpen}
                        onChange={() => toggleOpen(day)}
                        className="w-5 h-5 rounded border-gray-300 cursor-pointer"
                      />
                      <span className="font-semibold text-gray-900 capitalize text-lg">{day}</span>
                    </label>
                    {!d.isOpen && <span className="text-sm font-semibold text-red-600">🚫 Closed</span>}
                  </div>

                  {/* Time Slots */}
                  {d.isOpen && (
                    <div className="space-y-2 ml-8">
                      {(d.slots || []).map((slot, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <input
                            type="time"
                            value={slot.open}
                            onChange={(e) => updateSlot(day, idx, 'open', e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                          />
                          <span className="text-gray-400 font-semibold">→</span>
                          <input
                            type="time"
                            value={slot.close}
                            onChange={(e) => updateSlot(day, idx, 'close', e.target.value)}
                            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900"
                          />
                          {(d.slots || []).length > 1 && (
                            <button
                              onClick={() => removeSlot(day, idx)}
                              className="ml-auto px-3 py-2 rounded-lg bg-red-50 text-red-600 font-semibold hover:bg-red-100 transition text-sm"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => addSlot(day)}
                        className="mt-2 px-3 py-2 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition text-sm"
                      >
                        + Add Time Slot
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Save Button */}
          <div className="flex gap-3">
            <button
              onClick={save}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {loading ? "💾 Saving..." : "💾 Save Store Timing"}
            </button>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="font-semibold text-blue-900 mb-2">ℹ️ About Store Timing</h3>
            <ul className="text-sm text-blue-800 space-y-2">
              <li>• Use 24-hour format (e.g., 09:00 for 9 AM, 22:00 for 10 PM)</li>
              <li>• You can add multiple time slots per day for break hours</li>
              <li>• Example: 9:00-13:00 (lunch), 14:00-22:00 (evening)</li>
              <li>• Uncheck a day to mark the store as closed</li>
              <li>• Changes are saved immediately to the system</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoreTiming;
